/**
 * Simple typed event emitter for canvas-React communication
 */

import { CanvasEventType, CanvasEventPayloads, CanvasEventCallback } from './types';

type EventListeners = {
  [K in CanvasEventType]?: Set<CanvasEventCallback<K>>;
};

/**
 * EventEmitter provides typed event emission and subscription
 * for communication between the Konva canvas and React components
 */
export class EventEmitter {
  private listeners: EventListeners = {};

  /**
   * Subscribe to an event
   */
  on<T extends CanvasEventType>(event: T, callback: CanvasEventCallback<T>): () => void {
    if (!this.listeners[event]) {
      (this.listeners as Record<T, Set<CanvasEventCallback<T>>>)[event] = new Set();
    }
    
    const eventListeners = this.listeners[event] as Set<CanvasEventCallback<T>>;
    eventListeners.add(callback);

    // Return unsubscribe function
    return () => {
      eventListeners.delete(callback);
    };
  }

  /**
   * Subscribe to an event once
   */
  once<T extends CanvasEventType>(event: T, callback: CanvasEventCallback<T>): () => void {
    const wrappedCallback: CanvasEventCallback<T> = (payload) => {
      this.off(event, wrappedCallback);
      callback(payload);
    };
    
    return this.on(event, wrappedCallback);
  }

  /**
   * Unsubscribe from an event
   */
  off<T extends CanvasEventType>(event: T, callback: CanvasEventCallback<T>): void {
    const eventListeners = this.listeners[event] as Set<CanvasEventCallback<T>> | undefined;
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  /**
   * Emit an event with payload
   */
  emit<T extends CanvasEventType>(event: T, payload: CanvasEventPayloads[T]): void {
    const eventListeners = this.listeners[event] as Set<CanvasEventCallback<T>> | undefined;
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in event listener for "${event}":`, error);
        }
      });
    }
  }

  /**
   * Remove all listeners for an event or all events
   */
  removeAllListeners(event?: CanvasEventType): void {
    if (event) {
      delete this.listeners[event];
    } else {
      this.listeners = {};
    }
  }

  /**
   * Get listener count for an event
   */
  listenerCount(event: CanvasEventType): number {
    return this.listeners[event]?.size ?? 0;
  }
}
